import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Avatar,
  useTheme,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  Send,
  Support,
  Business,
  Feedback,
  TrackChanges,
  AttachFile,
  Close
} from '@mui/icons-material';

const ContactUs = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ticketId, setTicketId] = useState('');
  const [useEmail, setUseEmail] = useState(false);
  const [attachment, setAttachment] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowSuccess(false);
    setTicketId('');
    
    if (useEmail) {
      // Direct email option
      const mailtoLink = `mailto:contact.hypecircle@gmail.com?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
      )}`;
      window.open(mailtoLink);
      setShowSuccess(true);
      setTicketId('EMAIL');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Try API first
      const response = await fetch('https://yiyuz8psi0.execute-api.ap-south-1.amazonaws.com/prod/api/support/ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          priority: 'medium'
        })
      });

      if (response.ok) {
        const result = await response.json();
        setTicketId(result.ticketId);
        setShowSuccess(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setAttachment(null);
        setIsSubmitting(false);
        return;
      } else {
        console.error('API Error:', response.status, response.statusText);
        throw new Error('API request failed');
      }
    } catch (error) {
      console.error('Support ticket creation failed, falling back to email:', error);
      // Auto-fallback to email
      const mailtoLink = `mailto:contact.hypecircle@gmail.com?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
      )}`;
      window.open(mailtoLink);
      setShowSuccess(true);
      setTicketId('EMAIL-FALLBACK');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setAttachment(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Email />,
      title: "General Contact",
      info: "contact.hypecircle@gmail.com",
      description: "For general inquiries and support"
    },
    {
      icon: <Business />,
      title: "Team & Business",
      info: "hypecircle.team@gmail.com", 
      description: "For partnerships and business inquiries"
    },
    {
      icon: <Support />,
      title: "Support",
      info: "Available 24/7",
      description: "We're here to help with any issues"
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        sx={{
          textAlign: 'center',
          mb: 6,
          p: 4,
          borderRadius: 3,
          background: isDark
            ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 165, 0, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 197, 253, 0.05) 100%)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${isDark ? 'rgba(255, 215, 0, 0.2)' : 'rgba(59, 130, 246, 0.2)'}`,
        }}
      >
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 2,
            background: isDark
              ? 'linear-gradient(45deg, #FFD700, #FFA500)'
              : 'linear-gradient(45deg, #3B82F6, #93C5FD)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
          }}
        >
          Contact Us
        </Typography>
        <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Get in touch with the HypeCircle team
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              background: isDark
                ? 'rgba(0, 0, 0, 0.3)'
                : 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Send us a Message
              </Typography>
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message"
                      name="message"
                      multiline
                      rows={6}
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<AttachFile />}
                        sx={{
                          borderColor: isDark ? 'rgba(255, 215, 0, 0.5)' : 'rgba(59, 130, 246, 0.5)',
                          color: isDark ? '#FFD700' : '#3B82F6',
                        }}
                      >
                        Attach Screenshot
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => setAttachment(e.target.files[0])}
                        />
                      </Button>
                      {attachment && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{attachment.name}</Typography>
                          <Button
                            size="small"
                            onClick={() => setAttachment(null)}
                            sx={{ minWidth: 'auto', p: 0.5 }}
                          >
                            <Close fontSize="small" />
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        startIcon={<Send />}
                        disabled={isSubmitting}
                        sx={{
                          background: isDark
                            ? 'linear-gradient(45deg, #FFD700, #FFA500)'
                            : 'linear-gradient(45deg, #3B82F6, #1E40AF)',
                          color: isDark ? '#000' : '#fff',
                          fontWeight: 600,
                          px: 4,
                          py: 1.5,
                          '&:hover': {
                            background: isDark
                              ? 'linear-gradient(45deg, #FFA500, #FF8C00)'
                              : 'linear-gradient(45deg, #1E40AF, #1E3A8A)',
                          }
                        }}
                      >
                        {isSubmitting ? (useEmail ? 'Opening Email...' : 'Creating Ticket...') : 'Create Support Ticket'}
                      </Button>
                      
                      <Button
                        variant="outlined"
                        size="medium"
                        startIcon={<Email />}
                        onClick={() => {
                          setUseEmail(true);
                          handleSubmit({ preventDefault: () => {} });
                        }}
                        disabled={isSubmitting}
                        sx={{
                          borderColor: isDark ? 'rgba(255, 215, 0, 0.5)' : 'rgba(59, 130, 246, 0.5)',
                          color: isDark ? '#FFD700' : '#3B82F6',
                          '&:hover': {
                            borderColor: isDark ? '#FFD700' : '#3B82F6',
                            backgroundColor: isDark ? 'rgba(255, 215, 0, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                          }
                        }}
                      >
                        Send via Email Instead
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {contactInfo.map((contact, index) => (
              <Card
                key={index}
                sx={{
                  background: isDark
                    ? 'rgba(0, 0, 0, 0.3)'
                    : 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: isDark ? 'rgba(255, 215, 0, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                        color: isDark ? '#FFD700' : '#3B82F6',
                        mr: 2
                      }}
                    >
                      {contact.icon}
                    </Avatar>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                      {contact.title}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                    {contact.info}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {contact.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Grid>
      </Grid>

      {/* Track Your Ticket Section */}
      <Card
        sx={{
          mt: 6,
          background: isDark
            ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 165, 0, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 197, 253, 0.05) 100%)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${isDark ? 'rgba(255, 215, 0, 0.2)' : 'rgba(59, 130, 246, 0.2)'}`,
        }}
      >
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            Already Submitted a Ticket?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
            Track the status of your support request using your ticket ID and email address.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<TrackChanges />}
            onClick={() => window.open('/support/track', '_blank')}
            sx={{
              background: isDark
                ? 'linear-gradient(45deg, #FFD700, #FFA500)'
                : 'linear-gradient(45deg, #3B82F6, #1E40AF)',
              color: isDark ? '#000' : '#fff',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              '&:hover': {
                background: isDark
                  ? 'linear-gradient(45deg, #FFA500, #FF8C00)'
                  : 'linear-gradient(45deg, #1E40AF, #1E3A8A)',
              }
            }}
          >
            Track Your Ticket
          </Button>
        </CardContent>
      </Card>

      <Card
        sx={{
          mt: 4,
          background: isDark
            ? 'rgba(0, 0, 0, 0.3)'
            : 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        }}
      >
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            We'd Love to Hear From You
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Whether you have questions, feedback, or just want to say hello, 
            don't hesitate to reach out. Our team is always ready to help make 
            your HypeCircle experience even better.
          </Typography>
        </CardContent>
      </Card>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          sx={{ width: '100%' }}
          action={
            ticketId !== 'EMAIL' && ticketId !== 'EMAIL-FALLBACK' ? (
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => window.open('/support/track', '_blank')}
              >
                Track Ticket
              </Button>
            ) : null
          }
        >
          {ticketId === 'EMAIL' || ticketId === 'EMAIL-FALLBACK'
            ? 'Your email client will open with the message pre-filled. Send it to reach our team!'
            : `Support ticket created successfully! Ticket ID: ${ticketId}. Track your ticket status anytime.`
          }
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ContactUs;